/**
 * md-consent-calls.js — Super App Bounce Tracer, theme side of the contract.
 *
 * Contract: murphy-door-super-app docs/bounce-tracer/theme-contract.md
 * (plan .claude/plans/bounce-tracer.md P2.3, legal conditions LC-1..LC-14).
 * Decision record: docs/legal/bounce-tracer-decision-2026-10-10.md.
 *
 * ONE call, to the storefront-relative app proxy (Shopify signs it and strips
 * cookies), answered 204 whatever happens, never retried:
 *
 *  WITHDRAW  POST /apps/super-app/consent-withdraw  { visitorId }
 *     When analytics (or sale/share) consent goes from allowed to not allowed
 *     on this page -- a revoke through the banner or preferences, or a decline
 *     after an earlier accept -- send the md_vid once so the app tombstones the
 *     visitor and deletes their return-within-14-days rows. This is the PRIMARY
 *     deletion path (D1). Deleting is the safe direction, so a sale/share
 *     revoke counts too: the Super App pixel already treats saleOfData=false
 *     as unconsented. Kill switch: theme setting md_consent_withdraw_enabled.
 *
 * RETIRED PERMANENTLY (decision of 2026-10-10): the anonymous banner-choice
 * counter (the browser-sent "tally" request). Its code path was removed,
 * not just switched off. No request is ever sent because of a
 * decline, a GPC opt-out or leaving without a choice; the only request this
 * file can make is the deletion request above, on a real revoke. Do not
 * re-add a tally here: the Super App measures banner capture server-side
 * instead (pixel sessions vs Shopify sessions per landing page per day).
 *
 * Request shape is fixed by the contract: fetch with keepalive and
 * credentials:'omit'. NEVER navigator.sendBeacon -- it always sends cookies.
 *
 * Staff browsers (window.MD_INTERNAL_TRAFFIC, snippets/md-internal-traffic.liquid)
 * send nothing at all.
 *
 * Everything is wrapped: a privacy call must never break the page.
 */
(function () {
  'use strict';

  var WITHDRAW_URL = '/apps/super-app/consent-withdraw';
  var UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  /* Settings come from data attributes on this script tag (layout/theme.liquid). */
  var cfg = { withdraw: true };
  try {
    var me = document.currentScript;
    if (me) {
      cfg.withdraw = me.getAttribute('data-withdraw') !== 'false';
    }
  } catch (_) {}

  function isStaff() {
    return window.MD_INTERNAL_TRAFFIC === true;
  }

  function cp() {
    try {
      return (window.Shopify && window.Shopify.customerPrivacy) || null;
    } catch (_) {
      return null;
    }
  }

  function call(api, method) {
    try {
      if (!api || typeof api[method] !== 'function') return undefined;
      return api[method]();
    } catch (_) {
      return undefined;
    }
  }

  /* The one network primitive. Cookie-less by contract, fire-and-forget, no retry. */
  function post(url, body) {
    if (isStaff()) return false;
    try {
      if (typeof window.fetch !== 'function') return false;
      window
        .fetch(url, {
          method: 'POST',
          keepalive: true,
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        .catch(function () {});
      return true;
    } catch (_) {
      return false;
    }
  }

  function readVisitorId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)md_vid=([^;]*)/);
      var v = m ? decodeURIComponent(m[1]) : '';
      // Only the pixel's UUID v4 is a real id; a sess_ nonce or anything else
      // is rejected by the server, so do not send it at all.
      return UUID_V4.test(v) ? v : '';
    } catch (_) {
      return '';
    }
  }

  function sendWithdraw() {
    if (!cfg.withdraw || isStaff()) return false;
    var vid = readVisitorId();
    if (!vid) return false;
    return post(WITHDRAW_URL, { visitorId: vid });
  }

  /* ------------------------------------------------------------- wiring */

  var prior = null; // { analytics, sale } once the consent API answered

  function stateFromApi() {
    var api = cp();
    var a = call(api, 'analyticsProcessingAllowed');
    if (typeof a !== 'boolean') return null;
    return {
      analytics: a,
      sale: call(api, 'saleOfDataAllowed') !== false,
    };
  }

  function stateFromEvent(detail) {
    if (!detail || typeof detail.analyticsAllowed !== 'boolean') return stateFromApi();
    return {
      analytics: detail.analyticsAllowed,
      sale: detail.saleOfDataAllowed !== false,
    };
  }

  function onReady() {
    if (isStaff()) return;
    prior = stateFromApi();
  }

  function onConsentCollected(e) {
    if (isStaff()) return;
    var next = stateFromEvent(e && e.detail);
    if (!next) return;

    // Withdrawal: a real earlier grant changed to not-allowed on this page.
    if (
      prior &&
      ((prior.analytics && !next.analytics) || (prior.sale && !next.sale))
    ) {
      sendWithdraw();
    }
    prior = next;
  }

  if (isStaff() || !cfg.withdraw) return;

  try {
    document.addEventListener('visitorConsentCollected', onConsentCollected);
  } catch (_) {}

  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (!err) onReady();
        }
      );
    }
  } catch (_) {}
})();
